/* eslint-disable array-callback-return */
import {
  CalendarTodayRounded,
  FolderOpenRounded,
  GroupsRounded,
  ListAltRounded,
  Person,
  SecurityRounded,
  SpellcheckRounded,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  FormControl,
  TextField,
  Tooltip,
} from '@mui/material';

import { useCallback, useState, useEffect, ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Menu, LinkButton, Logo } from './styles';
import TenLogo2 from '../../assets/horizontal-blue-logo.svg';

import {
  Company,
  getCompaniesByUserIdRequest,
  getCompaniesRequest,
  getUserByIdRequest,
  Tokens,
  User,
} from '../../services';
import { createLogRequest } from '../../services/requests/app-logs/utils';

import useCompanyStore from '../../store/companies';
import useAuthStore from '../../store/auth';
import useLoadingStore from '../../store/loading';
import useUserStore from '../../store/users';

import { LogoCompanies } from '../LogoCompanies';

interface ItemsProps {
  name: string;
  label: string;
  icon: ReactElement;
}

const MENU_ITEMS: ItemsProps[] = [
  { name: 'reunioes', label: 'Reuniões', icon: <CalendarTodayRounded /> },
  {
    name: 'base-conhecimento',
    label: 'Arquivos',
    icon: <FolderOpenRounded />,
  },
  { name: 'assinaturas', label: 'Assinaturas', icon: <SpellcheckRounded /> },
  {
    name: 'boards',
    label: 'Boards',
    icon: <GroupsRounded />,
  },
  {
    name: 'gestao-usuarios',
    label: 'Usuários',
    icon: <Person />,
  },
  // { name: 'anotacoes', label: 'Anotações', icon: <ListAltRounded /> },
  { name: 'logs', label: 'Logs', icon: <SecurityRounded /> },
];

const MENU_ITEMS_FOR_MEMBERS: ItemsProps[] = [
  { name: 'reunioes', label: 'Reuniões', icon: <CalendarTodayRounded /> },
  {
    name: 'base-conhecimento',
    label: 'Arquivos',
    icon: <FolderOpenRounded />,
  },
  { name: 'assinaturas', label: 'Assinaturas', icon: <SpellcheckRounded /> },
  {
    name: 'boards',
    label: 'Boards',
    icon: <GroupsRounded />,
  },
  // { name: 'anotacoes', label: 'Anotações', icon: <ListAltRounded /> },
];

export function DashboardSideMenu() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const user = useAuthStore(state => state.user);
  const setUserStore = useAuthStore(state => state.setUserAuthenticated);
  const setSelectedUserInCompany = useUserStore(
    state => state.setSelectedUserInCompany,
  );
  const tokens = useAuthStore(state => state.tokens);
  const selectedCompany = useCompanyStore(state => state.selectedCompany);
  const setSelectedCompany = useCompanyStore(state => state.setSelectedCompany);
  const setLoading = useLoadingStore(state => state.setLoading);
  const setMenuSelected = useAuthStore(state => state.setMenuSelected);

  const [menuActive, setMenuActive] = useState('');
  const [listagemEmpresasUsuario, setListagemEmpresasUsuario] = useState<
    Company[]
  >([]);
  const [userState, setUserState] = useState({} as User);

  const loadUserById = useCallback(async () => {
    if (user?.id) {
      const response = await getUserByIdRequest(user?.id);

      if (response?.status === 200) {
        setUserState(response?.data);
      }
    }
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      loadUserById();
    }
  }, [user]);

  const loadUserCompanies = useCallback(async () => {
    if (user?.id) {
      const response = await getCompaniesByUserIdRequest(
        user?.id as string,
        '',
        '',
        '100',
      );

      if (response?.status === 200) {
        // console.log(response?.data, 'empresas');
        const allCompanies = await response?.data.companies;
        const activeCompanies = allCompanies.filter(
          (allC: Partial<Company>) => allC?.status,
        );
        setListagemEmpresasUsuario(activeCompanies);
      }
    }
  }, [user?.id]);

  useEffect(() => {
    if (user && user?.profile !== 'UN1') {
      loadUserCompanies();
    }
  }, [user]);

  const loadAllCompanies = useCallback(async () => {
    try {
      const response = await getCompaniesRequest('', '', '100');

      if (response?.status === 200) {
        // console.log(response?.data, 'empresas');

        const allCompanies = await response?.data.companies;
        const activeCompanies = allCompanies.filter(
          (allC: Partial<Company>) => allC?.status,
        );
        setListagemEmpresasUsuario(activeCompanies);
      }
    } catch (err) {
      console.log('Error loadAllCompanies =>', err);
    }
  }, []);

  useEffect(() => {
    if (user && user?.profile === 'UN1') {
      loadAllCompanies();
    }
  }, [user]);

  useEffect(() => {
    MENU_ITEMS.map((m: ItemsProps) => {
      if (pathname.includes(m.name)) {
        setMenuActive(m.name);
      }
    });
  }, [pathname]);

  const handleSelectMenu = useCallback(
    (name: string) => {
      setMenuActive(name);
      navigate(name);
      if (name === 'logs') {
        navigate(name);
        setMenuActive(name);
        setMenuSelected('logs');
      }
    },
    [navigate, setMenuSelected],
  );

  const createLogForChangeCompany = useCallback(
    async (companyId: string, companyName: string) => {
      await createLogRequest({
        name: user?.name as string,
        user_id: user?.id as string,
        company_id: companyId,
        local: 'Acessos',
        action: 'Acessou',
        description: `Acessou a plataforma na empresa ${companyName}`,
      });
    },
    [user?.id, user?.name],
  );

  const handleSelectCompany = useCallback(
    async (companyId: string) => {
      try {
        setLoading(true);
        const empresaSelecionada = listagemEmpresasUsuario?.find(
          (e: Company) => e.id === companyId,
        );

        const userInCompanyExists = empresaSelecionada?.users?.find(
          u => u.doc === user?.doc,
        );

        if (userInCompanyExists) {
          const userInCompany = {
            ...user,
            profile: userInCompanyExists?.profile,
          };

          setUserStore(userInCompany as User, tokens as Tokens);
          setSelectedUserInCompany(userInCompany as User);
        } else {
          setUserStore(userState as User, tokens as Tokens);
          setSelectedUserInCompany(userState);
        }

        // console.log({ userInCompany });
        setSelectedCompany(empresaSelecionada!);
        createLogForChangeCompany(
          empresaSelecionada?.id as string,
          empresaSelecionada?.name as string,
        );
        setTimeout(() => {
          setLoading(false);
          window.location.replace('/home/reunioes');
        }, 1000);
      } catch (err) {
        console.log(err);
      }
    },
    [
      setLoading,
      listagemEmpresasUsuario,
      setSelectedCompany,
      createLogForChangeCompany,
      user,
      setUserStore,
      tokens,
      setSelectedUserInCompany,
      userState,
    ],
  );

  return (
    <Menu>
      <Logo>
        <LogoCompanies
          url={selectedCompany?.logo_url ? selectedCompany?.logo_url : TenLogo2}
          boxWidth="100%"
          height="100%"
          maxWidth="100%"
          width=""
          justifyContent="start"
        />
      </Logo>
      <FormControl fullWidth>
        <Autocomplete
          id="country-select-demo"
          sx={{ background: '#EEEEEE' }}
          value={selectedCompany as Company}
          disabled={listagemEmpresasUsuario?.length === 0}
          onChange={(event, value) => handleSelectCompany(value?.id as string)}
          options={listagemEmpresasUsuario}
          autoHighlight
          disableClearable
          getOptionLabel={option => option?.fancy_name ?? option?.name}
          renderOption={(props, option) => (
            <Box
              component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
              {...props}
            >
              <img
                loading="lazy"
                style={{
                  width: '40px',
                  height: '40px',
                  border: '1px solid #E8E8E8',
                  borderRadius: '30px',
                  boxSizing: 'border-box',
                  padding: '3px 3px',
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: '50% 50%',
                  objectFit: 'contain',
                  mixBlendMode: 'multiply',
                }}
                src={(option?.logo_url as string) || TenLogo2}
                alt="Logo da empresa"
              />
              <Tooltip title={option?.name} placement="right" arrow>
                <Box
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {option?.fancy_name ?? option?.name}
                </Box>
              </Tooltip>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label="Escolha sua empresa"
              inputProps={{
                ...params.inputProps,
                sx: { width: '220px' },
                autoComplete: 'new-password',
              }}
              sx={{ width: '220px' }}
            />
          )}
        />
        {/* <Select
          value={selectedCompany?.id || ''}
          onChange={t => handleSelectCompany(t.target.value as string)}
          sx={{ background: '#EEEEEE' }}
          disabled={listagemEmpresasUsuario?.length === 0}
          MenuProps={{ sx: { height: '300px' } }}
        >
          {listagemEmpresasUsuario?.map((e: Company) => (
            <MenuItem key={e?.id} value={e?.id} sx={{ width: '200px' }}>
              <Tooltip title={e?.name} placement="right" arrow>
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {e?.name}
                </Box>
              </Tooltip>
            </MenuItem>
          ))}
        </Select> */}
      </FormControl>
      <h2 className="title-menu">Menu</h2>
      <ul className="menu-side">
        {(user?.profile === 'UN3' || user?.profile === 'UN4'
          ? MENU_ITEMS_FOR_MEMBERS
          : MENU_ITEMS
        ).map(value => (
          <li className="menu-item" key={value.name}>
            <LinkButton
              variant="text"
              type="button"
              onClick={() => handleSelectMenu(value.name)}
              active={menuActive === value.name}
            >
              {value.icon} {value.label}
            </LinkButton>
          </li>
        ))}
      </ul>
    </Menu>
  );
}
